import {Add, Delete} from "@mui/icons-material";
import {GridColDef} from "@mui/x-data-grid";
import React, {useEffect, useState} from "react";
import {ReferenceDefinitionDto} from "../../../../../common/apis/stereotype/ReferenceDefinitionDto";
import {StereotypeDto} from "../../../../../common/apis/stereotype/StereotypeDto";
import {BooleanFormatter} from "../../../../../common/BooleanFormatter";
import ExtGridWrapper from "../../../../../components/grid/ExtGridWrapper";
import {ActionButtonType, EnabledPolicy, GridAction} from "../../../../../components/grid/GridAction";
import {PersistentStateId} from "../../../../../store/common/Grid";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import {RelationshipTypeFormatter} from "../../relationship/RelationshipTypeFormatter";
import Snackbar from "../../snackbar/Snackbar";
import {useFindAllStereotypesQuery} from "../StereotypeQuery";
import {StereotypesTranslationKey} from "../StereotypesTranslationKey";
import {ReferenceDefinitionCreateDialog} from "./ReferenceDefinitionCreateDialog";
import ReferenceDefinitionDeleteDialog from "./ReferenceDefinitionDeleteDialog";
import {ReferenceDefinitionDetailDialog} from "./ReferenceDefinitionDetailDialog";
import {useFindAllReferenceDefinitionsQuery} from "./ReferenceDefinitionQuery";
import {ReferenceElementFilterFormatter} from "./ReferenceElementFilterFormatter";
import {ReferenceRelationshipDirectionFormatter} from "./ReferenceRelationshipDirectionFormatter";


export interface ReferenceDefinitionProps {
    stereotype: StereotypeDto;
    onUpdated?: () => void;
}

export default function ReferenceDefinitionGrid(props: Readonly<ReferenceDefinitionProps>) {
    const {stereotype, onUpdated} = props;

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedReference, setSelectedReference] = useState<ReferenceDefinitionDto | undefined>(undefined);

    const {data, isLoading, isError, error, refetch} = useFindAllReferenceDefinitionsQuery(stereotype.id);
    const {data: stereotypes} = useFindAllStereotypesQuery();

    useEffect(() => {
        if (isError && error) {
            Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
        }
    }, [isError, error]);

    function openDetailDialog(reference: ReferenceDefinitionDto) {
        setSelectedReference(reference);
        setDetailDialogOpen(true);
    }

    function closeDetailDialog(updated: boolean) {
        setDetailDialogOpen(false);
        if (updated) {
            onUpdated?.();
            refetch();
        }
    }

    function openCreateDialog() {
        setCreateDialogOpen(true);
    }

    function closeCreateDialog() {
        setCreateDialogOpen(false);
    }

    function onCreated() {
        onUpdated?.();
        refetch();
    }

    function openDeleteDialog(reference: ReferenceDefinitionDto) {
        setSelectedReference(reference);
        setDeleteDialogOpen(true);
    }

    function closeDeleteDialog() {
        setDeleteDialogOpen(false);
    }

    function onDeleted() {
        onUpdated?.();
        refetch();
    }

    const columnDefinitions: GridColDef<ReferenceDefinitionDto>[] = [
        {field: 'name', headerName: t(StereotypesTranslationKey.NAME), flex: 3},
        {field: 'description', headerName: t(CommonTranslationKey.DESCRIPTION), flex: 3},
        {field: 'relationshipType', headerName: t(StereotypesTranslationKey.RELATIONSHIP_TYPE), flex: 2, valueFormatter: (params) => RelationshipTypeFormatter.formatRelationshipType(params.value)},
        {field: 'relationshipDirection', headerName: t(StereotypesTranslationKey.RELATIONSHIP_DIRECTION), flex: 2, valueFormatter: params => ReferenceRelationshipDirectionFormatter.formatDirection(params.value, t)},
        {field: 'required', headerName: t(StereotypesTranslationKey.SMART_REFERENCE_REQUIRED), flex: 1, valueFormatter: (params) => BooleanFormatter.formatAsYesNo(params.value, t)},
        {field: 'multiple', headerName: t(StereotypesTranslationKey.MULTIPLE), flex: 1, valueFormatter: (params) => BooleanFormatter.formatAsYesNo(params.value, t)},
        {field: 'relatedElementFilter', headerName: t(StereotypesTranslationKey.ELEMENT_FILTER), flex: 2, valueFormatter: (params) => ReferenceElementFilterFormatter.formatElementFilter(params.value, stereotypes ?? [], t)},
    ];

    let references = data ? [...data] : [];
    references.sort((a, b) => a.name.localeCompare(b.name));

    return (
        <>
            <ExtGridWrapper
                    columns={columnDefinitions}
                    rows={references}
                    rowCount={references.length}
                    loading={isLoading}
                    getRowId={row => row.id}
                    actions={[
                        GridAction.detailButtonBuilder()
                                .enabledPolicy(EnabledPolicy.ALWAYS)
                                .isEnabled((selectedRows) => selectedRows.length === 1)
                                .onClick((_, selectedRows) => openDetailDialog(selectedRows[0]))
                                .build(),
                        GridAction.buttonBuilder('CREATE_REF_DEFINITION', ActionButtonType.IMMEDIATE, t(CommonTranslationKey.ADD), <Add/>)
                                .enabledPolicy(EnabledPolicy.ALWAYS)
                                .onClick(openCreateDialog)
                                .build(),
                        GridAction.buttonBuilder('DELETE_REF_DEFINITION', ActionButtonType.IMMEDIATE, t(CommonTranslationKey.REMOVE), <Delete/>)
                                .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                                .isEnabled((selectedRows) => selectedRows.length === 1 && selectedRows[0].acl?.canDelete)
                                .onClick((_, selectedRows) => openDeleteDialog(selectedRows[0]))
                                .build(),
                    ]}
                    onRowDoubleClick={(params) => openDetailDialog(params.row)}
                    peristentStateId={PersistentStateId.STEREOTYPES_PAGE_GRID}
                    resourceId={""} />

            <ReferenceDefinitionCreateDialog
                    open={createDialogOpen}
                    stereotypeId={stereotype.id}
                    stereotypes={stereotypes ?? []}
                    onCreated={onCreated}
                    onClose={closeCreateDialog} />

            {selectedReference &&
                    <>
                        <ReferenceDefinitionDetailDialog open={detailDialogOpen}
                                stereotypeId={stereotype.id}
                                stereotypes={stereotypes ?? []}
                                referenceDefinition={selectedReference}
                                onClose={closeDetailDialog}/>

                        <ReferenceDefinitionDeleteDialog open={deleteDialogOpen}
                                stereotypeId={stereotype.id}
                                referenceDefinitionId={selectedReference.id}
                                onClose={closeDeleteDialog}
                                onDeleted={onDeleted}/>
                    </>
            }
        </>
    );

}
